import { FormEvent, useEffect, useState } from 'react';
import type { ProductColour } from '@rosita/shared';
import { api, extractError } from '../../../lib/api';
import { useToast } from '../../../components/admin/Toast';
import { dangerBtnText, inputClass, primaryBtn, secondaryBtn } from './shared';

interface Draft {
  name: string;
  hex: string | null;
  sortOrder: number;
  isActive: boolean;
}

export default function ColoursTab(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<ProductColour[]>([]);
  const [name, setName] = useState('');
  const [hex, setHex] = useState('#000000');
  const [sortOrder, setSortOrder] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = async (): Promise<void> => {
    const response = await api.get<ProductColour[]>('/admin/colours');
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/colours', { name, hex, sortOrder, isActive: true });
      setName('');
      setHex('#000000');
      setSortOrder(0);
      toast.success('Cor criada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar cor'));
    }
  };

  const startEdit = (c: ProductColour): void => {
    setEditingId(c.id);
    setDraft({
      name: c.name,
      hex: c.hex ?? '#000000',
      sortOrder: c.sortOrder,
      isActive: c.isActive,
    });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !draft) return;
    try {
      await api.put(`/admin/colours/${editingId}`, draft);
      setEditingId(null);
      setDraft(null);
      toast.success('Cor atualizada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar cor'));
    }
  };

  const remove = async (c: ProductColour): Promise<void> => {
    if (!window.confirm(`Apagar cor "${c.name}"? As variantes existentes mantêm o valor.`)) return;
    try {
      await api.delete(`/admin/colours/${c.id}`);
      toast.success('Cor eliminada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao apagar cor'));
    }
  };

  return (
    <section>
      <form
        onSubmit={(e) => {
          void handleCreate(e);
        }}
        className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-[1fr_120px_100px_auto]"
      >
        <input
          placeholder="Nome (ex.: Vermelho)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputClass}
        />
        <input
          type="color"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          className="h-10 w-full cursor-pointer rounded-md border border-admin-border bg-white"
          title="Cor hex"
        />
        <input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          className={inputClass}
          placeholder="Ordem"
        />
        <button type="submit" className={primaryBtn}>
          Criar
        </button>
      </form>

      <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
        {items.length === 0 ? (
          <li className="p-4 text-sm text-admin-muted">Sem cores.</li>
        ) : null}
        {items.map((c) => {
          const isEditing = editingId === c.id;
          return (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-3 border-b border-admin-border p-3 last:border-b-0"
            >
              {isEditing && draft ? (
                <>
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    type="color"
                    value={draft.hex ?? '#000000'}
                    onChange={(e) => setDraft({ ...draft, hex: e.target.value })}
                    className="h-10 w-16 cursor-pointer rounded-md border border-admin-border bg-white"
                  />
                  <input
                    type="number"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                    className={`${inputClass} w-24`}
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    Ativa
                  </label>
                  <button type="button" onClick={() => void saveEdit()} className={primaryBtn}>
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setDraft(null);
                    }}
                    className={secondaryBtn}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span
                    aria-hidden
                    className="inline-block h-6 w-6 rounded border border-admin-border"
                    style={{ backgroundColor: c.hex ?? 'transparent' }}
                  />
                  <span className="flex-1 font-medium">
                    {c.name}{' '}
                    <small className="text-admin-muted">({c.hex ?? 'sem hex'})</small>
                  </span>
                  <span className="text-xs text-admin-muted">ordem {c.sortOrder}</span>
                  {!c.isActive ? (
                    <span className="rounded bg-admin-surface-alt px-2 py-0.5 text-xs text-admin-muted">
                      inativa
                    </span>
                  ) : null}
                  <button type="button" onClick={() => startEdit(c)} className={secondaryBtn}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void remove(c);
                    }}
                    className={dangerBtnText}
                  >
                    Apagar
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
